import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ServiceAPI from "../services/NavaService";
import Swal from "sweetalert2";
import TopNavbar from "../components/Nav/TopNavbar";
import Footer from "../components/Sections/Footer"

export default function ServicesAdmin() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedPreviews, setSelectedPreviews] = useState([]);
  const [currentSrvId, setCurrentSrvId] = useState(null);

  const [imagesMeta, setImagesMeta] = useState([]);
  const imagePreviewsRef = useRef([]);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"]
    ]
  };

  const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const quillFormats = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "link"];

  useEffect(() => {
    fetchServices();
    return () => {
      imagePreviewsRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (e) { }
      });
      imagePreviewsRef.current = [];
    };
  }, []);

  const customAlert = (v, w) => {
    Swal.fire({
      text: v,
      icon: w,
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        const popup = document.querySelector(".swal2-popup");
        if (container) container.style.zIndex = "10010";
        if (popup) popup.style.zIndex = "10011";
      }
    });
  };

  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await ServiceAPI.getServices();
      const data = res.data || [];
      const normalized = data.map((s) => ({
        ...s,
        srvId: s.srvId ?? s.id ?? s.ngs_id ?? s.ngsId ?? s.srv_id ?? null
      }));
      setServices(normalized);
    } catch (err) {
      console.error(err);
      customAlert("Failed to load services", "error");
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = services.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    const name = (s.name || s.service_name || "").toString().toLowerCase();
    const desc = (s.description || s.service_desc || "").toString().toLowerCase();
    const tags = (s.tags || []).join?.(" ").toLowerCase?.() || "";
    return name.includes(q) || desc.includes(q) || tags.includes(q);
  });
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageItems = filtered.slice((page - 1) * pageSize, page * pageSize);

  const openCreateModal = () => {
    setIsEditing(false);
    setForm({ name: "", description: "" });
    clearSelectedPreviews();
    setSelectedFiles([]);
    setCurrentSrvId(null);
    setImagesMeta([]);
    setIsModalOpen(true);
  };

  const openEditModal = async (srv) => {
    setIsEditing(true);
    setForm({
      name: srv.name || srv.service_name || "",
      description: srv.description || srv.service_desc || ""
    });
    clearSelectedPreviews();
    setSelectedFiles([]);
    const srvId = srv.srvId ?? srv.id ?? srv.ngs_id ?? srv.ngsId ?? null;
    setCurrentSrvId(srvId);
    setIsModalOpen(true);
    if (srvId) await loadImagesForService(srvId);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setForm({ name: "", description: "" });
    setSelectedFiles([]);
    clearSelectedPreviews();
    setCurrentSrvId(null);
    imagePreviewsRef.current.forEach((u) => {
      try { URL.revokeObjectURL(u); } catch (e) { }
    });
    imagePreviewsRef.current = [];
    setImagesMeta([]);
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const accepted = [];
    const rejected = [];

    files.forEach((f) => {
      if (f.size <= MAX_IMAGE_BYTES) accepted.push(f);
      else rejected.push(f);
    });

    const newPreviews = accepted.map((f) => {
      const url = URL.createObjectURL(f);
      imagePreviewsRef.current.push(url);
      return { name: f.name, url, file: f, size: f.size };
    });

    if (accepted.length > 0) {
      setSelectedFiles((prev) => [...prev, ...accepted]);
      setSelectedPreviews((prev) => [...prev, ...newPreviews]);
    }

    if (rejected.length > 0) {
      const listHtml = rejected
        .map((r) => `<li>${r.name} — ${formatBytes(r.size)}</li>`)
        .join("");
      Swal.fire({
        title: "Some files were too large",
        html: `<div>Files larger than 2 MB were not added:</div><ul style="text-align:left;margin-top:8px">${listHtml}</ul>`,
        icon: "warning",
        didOpen: () => {
          const container = document.querySelector(".swal2-container");
          const popup = document.querySelector(".swal2-popup");
          if (container) container.style.zIndex = "10010";
          if (popup) popup.style.zIndex = "10011";
        }
      });
    }

    e.target.value = "";
  };

  const removeSelectedPreview = (idx) => {
    const toRemove = selectedPreviews[idx];
    if (toRemove) {
      try { URL.revokeObjectURL(toRemove.url); } catch (e) { }
      imagePreviewsRef.current = imagePreviewsRef.current.filter((u) => u !== toRemove.url);
      setSelectedPreviews((prev) => prev.filter((_, i) => i !== idx));
      setSelectedFiles((prevFiles) => {
        const foundIndex = prevFiles.findIndex((f) => f.name === toRemove.name && f.size === toRemove.file.size);
        if (foundIndex >= 0) {
          const copy = [...prevFiles];
          copy.splice(foundIndex, 1);
          return copy;
        }
        return prevFiles;
      });
    }
  };

  const clearSelectedPreviews = () => {
    selectedPreviews.forEach((p) => {
      try { URL.revokeObjectURL(p.url); } catch (e) { }
    });
    setSelectedPreviews([]);
  };

  const createService = async () => {
    try {
      const payload = { name: form.name, description: form.description };
      const res = await ServiceAPI.postServices(payload);
      const created = res.data;
      const srvId = created?.srvId ?? created?.id ?? created?.ngs_id ?? created?.srv_id ?? null;
      const uploadTarget = srvId ?? created;

      if (selectedFiles.length > 0 && uploadTarget) {
        await ServiceAPI.uploadImages(uploadTarget, selectedFiles);
      }

      customAlert("Service Created successfully", "success");
      closeModal();
      fetchServices();
    } catch (err) {
      console.error(err);
      customAlert("Failed to create service", "error");
    }
  };

  const updateService = async () => {
    try {
      const payload = { name: form.name, description: form.description };
      await ServiceAPI.updateService(currentSrvId, payload);

      if (selectedFiles.length > 0) {
        await ServiceAPI.uploadImages(currentSrvId, selectedFiles);
      }

      customAlert("Service Updated successfully", "success");
      closeModal();
      fetchServices();
    } catch (err) {
      console.error(err);
      customAlert("Failed to update service", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) await updateService();
    else await createService();
  };

  const confirmTop = async (opts) => {
    const result = await Swal.fire({
      ...opts,
      didOpen: () => {
        const container = document.querySelector(".swal2-container");
        const popup = document.querySelector(".swal2-popup");
        if (container) container.style.zIndex = "10010";
        if (popup) popup.style.zIndex = "10011";
      }
    });
    return result;
  };

  const handleDeleteService = async (srvId) => {
    const confirm = await confirmTop({
      title: "Delete service?",
      text: "This will remove the service and its images.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });
    if (!confirm.isConfirmed) return;

    try {
      await ServiceAPI.deleteService(srvId);
      customAlert("Service deleted", "success");
      fetchServices();
    } catch (err) {
      console.error(err);
      customAlert("Failed to delete service", "error");
    }
  };

  const loadImagesForService = async (srvId) => {
    try {
      const res = await ServiceAPI.listImages(srvId);
      const meta = res.data || [];

      imagePreviewsRef.current.forEach((u) => {
        try { URL.revokeObjectURL(u); } catch (e) { }
      });
      imagePreviewsRef.current = [];

      const previews = await Promise.all(
        meta.map(async (m) => {
          const imgId = m?.imgId ?? m?.id ?? m?.img_id ?? m?.nsi_id ?? m?.imageId ?? null;
          const filename = m?.filename ?? m?.name ?? m?.fileName ?? "image";

          if (!imgId) {
            return { ...m, imgId: null, filename, previewUrl: null };
          }

          try {
            const resp = await ServiceAPI.getImage(srvId, imgId);
            const url = URL.createObjectURL(resp.data);
            imagePreviewsRef.current.push(url);
            return { ...m, imgId, filename, previewUrl: url };
          } catch (err) {
            console.warn("Failed to load image blob", m, err);
            return { ...m, imgId, filename, previewUrl: null };
          }
        })
      );

      setImagesMeta(previews);
    } catch (err) {
      console.error(err);
      customAlert("Failed to load images for service", "error");
    }
  };

  const handleDeleteImage = async (imgId) => {
    const srvId = currentSrvId;
    if (!srvId) return;
    const confirm = await confirmTop({
      title: "Delete image?",
      text: "This will permanently remove the image.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });
    if (!confirm.isConfirmed) return;

    try {
      await ServiceAPI.deleteImage(srvId, imgId);
      customAlert("Image deleted", "success");
      await loadImagesForService(srvId);
    } catch (err) {
      console.error(err);
      customAlert("Failed to delete image", "error");
    }
  };

  const handleDeleteFromModal = async () => {
    const srvId = currentSrvId;
    if (!srvId) {
      customAlert("No service selected to delete", "error");
      return;
    }

    const confirm = await confirmTop({
      title: "Delete service?",
      text: "This will remove the service and its images.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete"
    });
    if (!confirm.isConfirmed) return;

    try {
      await ServiceAPI.deleteService(srvId);
      customAlert("Service deleted", "success");
      closeModal();
      fetchServices();
    } catch (err) {
      console.error(err);
      customAlert("Failed to delete service", "error");
    }
  };


  return (
    <>
      <TopNavbar />
      <PageWrapper style={{ marginTop: "5%" }}>
        <TopRow>
          <h2>Services Offered</h2>
          <div className="controls">
            <input
              type="text"
              placeholder="Search name, description or tags..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <OutlineBtn onClick={() => { setSearch(""); setPage(1); }}>Clear</OutlineBtn>
            <PrimaryBtn onClick={openCreateModal}>New Service</PrimaryBtn>
          </div>
        </TopRow>

        <TableWrap>
          {loading ? (
            <div>Loading…</div>
          ) : (
            <>
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((s) => {
                    const id = s.srvId ?? s.id ?? s.ngs_id ?? s.ngsId ?? "-";
                    const name = s.name ?? s.service_name ?? "";
                    const desc = s.description ?? s.service_desc ?? "";
                    return (
                      <tr key={id}>
                        <td>{id}</td>
                        <td>{name}</td>
                        <td dangerouslySetInnerHTML={{ __html: (desc || "").slice(0, 30) + ((desc || "").length > 30 ? "…" : "") }} />
                        <td>
                          <PrimarySmall onClick={() => openEditModal(s)}>View / Edit</PrimarySmall>
                          <OutlineSmall onClick={() => handleDeleteService(id)}>Delete</OutlineSmall>
                        </td>
                      </tr>
                    );
                  })}
                  {pageItems.length === 0 && (
                    <tr>
                      <td colSpan="6">No services found.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              <Pagination>
                <OutlineSmall disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Prev</OutlineSmall>
                <span>Page {page} / {pageCount}</span>
                <OutlineSmall disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))}>Next</OutlineSmall>
              </Pagination>
            </>
          )}
        </TableWrap>

        {isModalOpen && (
          <ModalOverlay onClick={closeModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h3>{isEditing ? `Edit ${form.name ?? ""}` : "Create Service"}</h3>
                <button onClick={closeModal}>Close</button>
              </ModalHeader>

              <form onSubmit={handleSubmit}>
                <ModalBody>
                  {!isEditing ? null : (
                    <>
                      <label>ID </label>
                      <IdBox>{currentSrvId}</IdBox>
                    </>
                  )}

                  <label>Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                  />

                  <label>Description</label>
                  <BoxEditor>
                    <ReactQuill
                      theme="snow"
                      modules={quillModules}
                      formats={quillFormats}
                      value={form.description}
                      onChange={(html) => setForm((prev) => ({ ...prev, description: html }))}
                    />
                  </BoxEditor>

                  <label>Images</label>
                  <ImagesArea>
                    <div className="upload-row">
                      <input type="file" multiple accept="image/*" onChange={onFileChange} />
                    </div>

                    {selectedPreviews.length > 0 && (
                      <>
                        <div style={{ marginTop: 12, fontWeight: 600 }}>Selected (preview before upload)</div>
                        <div className="images-grid" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          {selectedPreviews.map((p, idx) => (
                            <div key={`${p.name}-${idx}`} className="img-card" style={{ width: 120, border: "1px solid #eee", padding: 6, borderRadius: 6, background: "#fff" }}>
                              <img src={p.url} alt={p.name} style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 4 }} />
                              <div style={{ fontSize: 12, marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.name}</div>
                              <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                                <button type="button" onClick={() => removeSelectedPreview(idx)} style={{ padding: "6px 8px", borderRadius: 6, background: "#999", color: "#fff", border: "0", cursor: "pointer" }}>
                                  Remove
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {isEditing && imagesMeta.length > 0 && (
                      <>
                        <div style={{ marginTop: 12, fontWeight: 600 }}>Existing Images</div>
                        <div className="images-grid" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          {imagesMeta.map((m) => (
                            <div key={m.imgId ?? m.id ?? m.img_id ?? `${m.filename}-${Math.random()}`} className="img-card" style={{ width: 120, border: "1px solid #eee", padding: 6, borderRadius: 6, background: "#fafafa" }}>
                              {m.previewUrl ? (
                                <img src={m.previewUrl} alt={m.filename} style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 4 }} />
                              ) : (
                                <div style={{ height: 80, display: "flex", alignItems: "center", justifyContent: "center" }}>No preview</div>
                              )}
                              <div style={{ fontSize: 12, marginTop: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.filename ?? m.name}</div>
                              <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                                <button type="button" className="small-delete" onClick={() => handleDeleteImage(m.imgId ?? m.id ?? m.img_id ?? m.nsi_id)} style={{ padding: "6px 8px", borderRadius: 6, background: "#e55353", color: "#fff", border: "0", cursor: "pointer" }}>
                                  Delete
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </ImagesArea>
                </ModalBody>

                <ModalFooter>
                  {isEditing && (
                    <OutlineBtn
                      type="button"
                      onClick={handleDeleteFromModal}
                      style={{
                        marginRight: "auto",
                        borderColor: "#e8b5b5",
                        color: "#b32424",
                        background: "#fff"
                      }}
                    >
                      Delete Service
                    </OutlineBtn>
                  )}
                  <OutlineBtn type="button" onClick={closeModal}>Cancel</OutlineBtn>
                  <PrimaryBtn type="submit">{isEditing ? "Save Changes" : "Create Service"}</PrimaryBtn>
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </PageWrapper>
    </>
  );
}

const PageWrapper = styled.div`
  padding: 28px;
  max-width: 1200px;
  margin: 0 auto;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial;
  color: #222;

  h2 { margin: 0 0 8px 0; font-size: 20px; font-weight: 700; }

  .controls { display:flex; gap:10px; align-items:center; margin-bottom:12px; }
  input[type="text"] { padding:10px 12px; border:1px solid #e6e6e9; border-radius:8px; min-width:260px; font-size:14px; background:#fff; }
`;

const PrimaryBtn = styled.button`
  padding:10px 14px;
  border-radius:10px;
  border:0;
  background: linear-gradient(180deg,#8338ff,#5a17d6);
  color:#fff;
  cursor:pointer;
  font-weight:600;
  box-shadow: 0 8px 24px rgba(88,24,201,0.14);
  transition: transform .08s ease, box-shadow .08s;
  &:active { transform: translateY(1px); }
  &:disabled { opacity:.6; cursor:not-allowed; }
`;

const OutlineBtn = styled.button`
  padding:10px 14px;
  border-radius:10px;
  border:1px solid #dde0ea;
  background:#fff;
  color:#222;
  cursor:pointer;
  font-weight:600;
`;

const TableWrap = styled.div`
  background: #fff;
  border: 1px solid #f0f0f6;
  padding: 12px;
  border-radius: 12px;
  /* vertical scroll region */
  max-height: calc(100vh - 320px);
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  table {
    width:100%;
    border-collapse:collapse;
    font-size:14px;
    table-layout: fixed; /* stable column widths */
    min-width: 880px;
  }

  thead th {
    position: sticky;
    top: 0;
    background: linear-gradient(180deg,#fff,#fbfbfe);
    z-index: 3;
    padding: 12px;
    font-weight: 700;
    color: #333;
    text-align: left;
    border-bottom: 1px solid #f3f4f8;
  }

  th, td {
    padding: 12px;
    border-bottom: 1px solid #f6f7fb;
    vertical-align: top;
    word-break: break-word;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  /* description column: allow multi-line preview with clamp */
  th:nth-child(3), td:nth-child(3) {
    white-space: normal;
    max-width: 44%;
  }

  td.description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    white-space: normal;
  }

  /* actions column: keep buttons on one row */
  td.actions {
    white-space: nowrap;
    width: 170px;
  }

  tbody tr:hover { background: #fafbff; }

  /* small screens: allow horizontal scroll, reduce paddings */
  @media (max-width: 960px) {
    table { min-width: 700px; }
    th, td { padding: 10px; font-size: 13px; }
  }

  @media (max-width: 560px) {
    table { min-width: 620px; }
  }
`;

const Pagination = styled.div`
  display:flex;
  justify-content:center;
  align-items:center;
  gap:12px;
  padding:14px 0;
  color:#444;
  font-size:14px;
`;

const ModalOverlay = styled.div`
  position:fixed;
  inset:0;
  background: rgba(7, 10, 25, 0.48);
  display:flex;
  align-items:center;
  justify-content:center;
  z-index:6000;
  padding: 20px;
`;

const ModalContent = styled.div`
  width:880px;
  max-width:98%;
  max-height:92vh;
  background:#fff;
  border-radius:12px;
  padding:20px;
  overflow:auto;
  box-shadow: 0 30px 80px rgba(16,24,64,0.18);
`;

const ModalHeader = styled.div`
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:10px;
  h3 { margin:0; font-size:18px; font-weight:700; color:#111; }
  button { background:transparent; border:0; color:#666; font-size:15px; cursor:pointer; }
`;

const ModalBody = styled.div`
  display:flex;
  flex-direction:column;
  gap:10px;
  label { font-weight:600; font-size:13px; margin-top:8px; color:#333; }
  input[type="text"], textarea, select {
    padding:10px 12px;
    border:1px solid #e7e7ee;
    border-radius:8px;
    font-size:14px;
    background:#fff;
    outline:none;
  }
  textarea { min-height:140px; resize:vertical; font-family: inherit; }
`;

const IdBox = styled.div`
  padding:8px 10px;
  background:#f7f8fb;
  border-radius:8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, "Roboto Mono", monospace;
  color:#333;
  border:1px solid #f0f1f6;
`;

const BoxEditor = styled.div`
  border:1px solid #eee;
  border-radius:8px;
  padding:6px;
  background:#fff;
  .ql-container { border-radius:6px; min-height:120px; }
`;

const TagRow = styled.div` display:flex; flex-direction:column; gap:8px; `;
const TagInput = styled.input` padding:10px; border-radius:8px; border:1px solid #e9e9f0; `;
const TagList = styled.div` display:flex; gap:8px; flex-wrap:wrap; margin-top:6px; `;
const TagNote = styled.small` color:#6b6f76; display:block; margin-top:6px; `;
const TagPill = styled.div` background:#f1efff; padding:6px 10px; border-radius:999px; font-size:13px; display:inline-flex; gap:8px; align-items:center; small{ cursor:pointer; color:#666; }`;

const ImagesArea = styled.div`
  .images-grid { display:flex; gap:10px; flex-wrap:wrap; margin-top:8px; }
  .img-card { width:128px; border:1px solid #f1f2f6; padding:8px; border-radius:8px; position:relative; background:#fff; box-shadow: 0 2px 6px rgba(16,24,64,0.04); }
  .img-card img { width:100%; height:88px; object-fit:cover; border-radius:6px; display:block; }
  .img-actions { display:flex; justify-content:center; margin-top:8px; gap:8px; }
  .upload-row { margin-top:8px; display:flex; gap:10px; align-items:center; }
  input[type="file"] { font-size:13px; }
`;

const ModalFooter = styled.div` display:flex; justify-content:flex-end; gap:10px; margin-top:14px; `;

const PrimarySmall = styled.button`
  padding:7px 10px;
  border-radius:8px;
  border:0;
  background:#6f2bdc;
  color:#fff;
  cursor:pointer;
  font-weight:600;
  white-space:nowrap;
`;
const OutlineSmall = styled.button`
  padding:7px 10px;
  border-radius:8px;
  border:1px solid #e6e7ef;
  background:#fff;
  color:#222;
  cursor:pointer;
`;

const TopRow = styled.div`
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:16px;
  gap:16px;

  h2 { font-size:20px; margin:0; }

  .controls { display:flex; gap:10px; align-items:center; }

  @media (max-width:720px) {
    flex-direction:column;
    align-items:flex-start;
    .controls { width:100%; display:flex; gap:8px; flex-wrap:wrap; }
  }
`;
